import { ReactNode, useCallback, useEffect, useState, type CSSProperties } from 'react';
import { AppSidebar, HireMobileBottomNav } from './AppSidebar';
import { HIRE_PAGE_CONTAINER } from '@/lib/hireResponsive';
import { SidebarProvider } from '@/components/ui/sidebar';
import { OnboardingGuide } from './OnboardingGuide';
import { useDirection } from '@/hooks/useDirection';
import { ActiveInterviewsBanner } from '@/components/ActiveInterviewsBanner';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';

const HIRE_SIDEBAR_STORAGE_KEY = 'forkhire:sidebar-open';
const HIRE_DESKTOP_SIDEBAR_QUERY = '(min-width: 1280px)';
const HIRE_SIDEBAR_RAIL_STYLE = { '--sidebar-width-icon': '4rem' } as CSSProperties;

function getStoredHireSidebarOpen() {
  const storedValue = window.localStorage.getItem(HIRE_SIDEBAR_STORAGE_KEY);
  return storedValue === null ? true : storedValue === 'true';
}

function getInitialHireSidebarOpen() {
  if (typeof window === 'undefined') return true;
  return window.matchMedia(HIRE_DESKTOP_SIDEBAR_QUERY).matches && getStoredHireSidebarOpen();
}

const hireLogoAssets = {
  en: {
    light: '/SVG-New/Hire-black%20En.svg',
    dark: '/SVG-New/Hire-White%20En.svg',
  },
  ar: {
    light: '/SVG-New/Hire-Black%20Ar.svg',
    dark: '/SVG-New/Hire-White%20Ar.svg',
  },
} as const;

export function AppLayout({ children }: { children: ReactNode }) {
  const { dir } = useDirection();
  const { theme, resolvedTheme } = useTheme();
  const { i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(getInitialHireSidebarOpen);
  const isArabic = (i18n.resolvedLanguage ?? i18n.language)?.startsWith('ar');
  const isDark = (resolvedTheme ?? theme) === 'dark';
  const hireLogoSrc = hireLogoAssets[isArabic ? 'ar' : 'en'][isDark ? 'dark' : 'light'];

  useEffect(() => {
    const desktopMedia = window.matchMedia(HIRE_DESKTOP_SIDEBAR_QUERY);
    const syncSidebarMode = (event?: MediaQueryListEvent) => {
      const isDesktop = event?.matches ?? desktopMedia.matches;
      setSidebarOpen(isDesktop ? getStoredHireSidebarOpen() : false);
    };

    syncSidebarMode();
    desktopMedia.addEventListener('change', syncSidebarMode);
    return () => desktopMedia.removeEventListener('change', syncSidebarMode);
  }, []);

  const handleSidebarOpenChange = useCallback((open: boolean) => {
    setSidebarOpen(open);
    if (window.matchMedia(HIRE_DESKTOP_SIDEBAR_QUERY).matches) {
      window.localStorage.setItem(HIRE_SIDEBAR_STORAGE_KEY, String(open));
    }
  }, []);

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarOpenChange} style={HIRE_SIDEBAR_RAIL_STYLE}>
      <div dir={dir} className="flex h-dvh w-full overflow-hidden bg-background">
        <AppSidebar />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header
            className="sticky top-0 z-30 flex h-16 items-center justify-center border-b border-border bg-card px-3 shadow-sm safe-area-inset sm:px-4 md:hidden"
          >
            <img src={hireLogoSrc} alt="ForkHire" className="h-auto w-24 max-w-[44vw] shrink-0" />
          </header>
          <main className="mb-[calc(5rem+env(safe-area-inset-bottom))] min-h-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto p-3 pb-8 safe-area-inset sm:p-6 sm:pb-8 md:mb-0 md:p-8 lg:p-10 xl:p-12 2xl:p-14 3xl:p-16 4xl:p-20">
            <div className={HIRE_PAGE_CONTAINER}>
              <ActiveInterviewsBanner />
            </div>
            {children}
          </main>
        </div>
        <HireMobileBottomNav />
      </div>
      <OnboardingGuide />
    </SidebarProvider>
  );
}
